import {
  angleModeFor, answerSymmetry,
  computeAcceptedRanges, computeRotationAcceptedRanges, computeSliderAcceptedRanges,
  computeTiltAcceptedRanges, evaluateFormulaRaw, haversineDistanceMeters,
  signatureForBands, signatureForDistance, signatureForHeading, type AcceptedBand,
} from '@agavi/blocks';

import { numericalRangeConfig, rotationAcceptedRangeConfig, tiltAcceptedRangeConfig } from './block-condition-chips';

import type { Block } from '@/types/experience';

import { handleRangeConfigFrom } from '@/components/task/slider-block';

/**
 * An answer formula resolved in ITS OWN field's angle mode.
 *
 * A signature says what KIND of attempt this was, for counting unique ones — so it is not
 * grading, but it still has to agree with what the teacher authored. Resolving in a different
 * mode than the field declares makes two genuinely different attempts look alike.
 */
const resolveField = (data: Record<string, unknown>, roll: Record<string, string>) =>
  (expr: string, fieldId = '') => evaluateFormulaRaw(expr, roll, angleModeFor(data, fieldId));


/**
 * "What KIND of attempt was this?", per block, for `unique`.
 *
 * ## Why this is one dispatcher rather than a method on each block
 *
 * A signature is a pure function of the block's configuration and the answer, and BOTH of those
 * are already in hand at the one place that needs it -- the page that records an attempt holds
 * the blocks, the answers the registry collected, and the roll. Threading an `attemptSignature()`
 * through eleven components would move no information and add eleven call sites to keep in step.
 *
 * ## The families
 *
 * Discrete answers are already their own signature: a selection, a sequence, and a normalised
 * string are the same attempt exactly when they are equal. Only the RANGED ones need bucketing,
 * and they all share one rule -- see `attempt-signature.ts` in `@agavi/blocks`.
 *
 * ## Silence is a real answer here
 *
 * A block this cannot sign returns `''`, and `countsFor` then leaves it out of its own `unique`
 * count AND refuses to build a task-level signature at all. That is deliberate: a half-built
 * signature would make two different attempts look identical and under-count without saying so.
 * A rule that visibly does nothing is recoverable; one that quietly miscounts is not.
 */

/** Trimmed, lowercased, inner whitespace collapsed -- "the same word, typed differently". */
function normaliseText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** The accepted bands for a numerical block, with each exact answer's own value as the anchor. */
function numericalBands(data: Record<string, unknown>, roll: Record<string, string>): AcceptedBand[] {
  const config = numericalRangeConfig(data as never, roll);
  const ranges = computeAcceptedRanges(config);
  if (config.conditionType !== 'exact') return ranges;

  // `at` matters only for an asymmetric tolerance, where the band's midpoint is NOT the number
  // the teacher typed. `computeAcceptedRanges` filters on the same predicate in the same order,
  // so the two line up index for index.
  const answers = config.answers.filter(a => Number.isFinite(a.value));
  return ranges.map((r, i) => ({ ...r, at: answers[i]?.value }));
}

/**
 * A SCALAR, SIGNED -- or nothing.
 *
 * `String()` on a reported object is `"[object Object]"`, the same string for every submission,
 * and a signature exists only to be compared for equality: one that cannot tell two attempts
 * apart is worse than none, because `unique` then reports a confident wrong number instead of an
 * obvious zero. Both blocks that drifted to an object answer failed exactly here.
 */
function scalar(v: unknown): string | null {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    ? `s:${String(v)}`
    : null;
}

export function attemptSignature(
  block: Block,
  answer: unknown,
  roll: Record<string, string> = {},
): string {
  if (answer === undefined || answer === null) return '';

  switch (block.type) {
    // ── Selection: the chosen option IS the attempt ──────────────────────────────────────────
    /**
     * `{ selectedIds, selectedTexts }`, which is what MCQ reports -- and what this missed.
     *
     * It read a bare array of ids, which MCQ stopped sending when the option TEXT started
     * travelling beside the ids for export and cross-task references. `Array.isArray` was then
     * false for every submission, the signature came back empty, and `unique` -- which counts
     * only non-empty signatures -- sat at zero for ever. Reported as "i never see the unique
     * counter going up". The array form is still read, because attempts recorded before the
     * shape changed are in the log.
     *
     * Sorted, because picking A then B is the same answer as picking B then A.
     */
    case 'mcq': {
      const ids = Array.isArray(answer)
        ? answer as string[]
        : (answer as { selectedIds?: unknown }).selectedIds;
      return Array.isArray(ids) ? `s:${[...ids as string[]].sort().join(',')}` : '';
    }
    /**
     * `{ selectedId, selectedTexts }`, the same drift MCQ had and a worse symptom.
     *
     * `String()` on the reported object is `"[object Object]"` -- the SAME string for every
     * submission -- so a dropdown did not read zero unique attempts, it read ONE however many
     * different options a student tried. A quiet miscount, which is exactly what `tally`'s own
     * note says is unrecoverable where a visible zero is not.
     */
    case 'dropdown': {
      const id = (answer as { selectedId?: unknown }).selectedId;
      return scalar(id) ?? scalar(answer) ?? '';
    }
    /** A scale reports its point, which is already a scalar. */
    case 'scale':
      return scalar(answer) ?? '';

    // ── Sequence: order is the whole answer, so it is not sorted ─────────────────────────────
    case 'ordering':
      return Array.isArray(answer) ? `q:${(answer as string[]).join(',')}` : '';

    // ── Partition: which bin, and only sometimes in what order ───────────────────────────────
    /**
     * A sort's answer is `binId -> itemId[]`, and whether the sequence inside a bin counts is the
     * block's own `orderWithinBins`. With it off, filing A then B into one bin is the SAME attempt
     * as filing B then A — so the ids are sorted, exactly as MCQ sorts a multi-select. With it on
     * they are left alone, exactly as Ordering leaves a sequence alone.
     *
     * Bins are sorted by id regardless: which bin a teacher happened to author first is not part of
     * what a student did.
     */
    case 'sort': {
      if (typeof answer !== 'object') return '';
      const graded = !!(block.data as { orderWithinBins?: boolean }).orderWithinBins;
      const placements = answer as Record<string, string[]>;
      const parts = Object.keys(placements).sort().map((binId) => {
        const ids = placements[binId];
        const list = Array.isArray(ids) ? ids : [];
        return `${binId}=${(graded ? list : [...list].sort()).join(',')}`;
      });
      return `${graded ? 'q' : 's'}:${parts.join('|')}`;
    }

    // ── Normalised text ──────────────────────────────────────────────────────────────────────
    case 'keyword':
      return typeof answer === 'string' ? `t:${normaliseText(answer)}` : '';
    case 'fitb': {
      if (typeof answer !== 'object') return '';
      const values = answer as Record<string, string>;
      // Keyed and sorted so the signature does not depend on blank iteration order.
      // `String()` on each, because a blank's value is text but a drifted shape need not be --
      // and `normaliseText` calling `.trim()` on a non-string threw, inside grading.
      return `t:${Object.keys(values).sort().map(k => `${k}=${normaliseText(String(values[k] ?? ''))}`).join('|')}`;
    }

    // ── Ranged: bucketed by the block's own tolerance ────────────────────────────────────────
    case 'numerical': {
      const value = (answer as { value?: unknown }).value;
      const numeric = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
      if (!Number.isFinite(numeric)) return '';
      // The unit travels with it: 5 metres and 5 feet are not the same attempt, whatever the
      // grader makes of either.
      const unit = String((answer as { unit?: unknown }).unit ?? '');
      return `${signatureForBands(numeric, numericalBands(block.data, roll))}@${unit}`;
    }

    case 'slider': {
      // Two handles, each with its own answers and its own tolerance, so each gets its own
      // signature and they are joined. Changing either handle is a different attempt.
      const pair = answer as { a?: { value?: number; touched?: boolean }; b?: { value?: number; touched?: boolean } };
      const data = block.data as Record<string, unknown>;
      const dual = data.dualHandle === true;
      const resolve = resolveField(block.data as Record<string, unknown>, roll);
      const sign = (handle: 'a' | 'b', side?: { value?: number; touched?: boolean }) => {
        if (!side || typeof side.value !== 'number') return '';
        // An UNTOUCHED handle sitting on the default is not an answer, and must not read as one:
        // two students who moved only handle B have made the same attempt at handle A.
        if (side.touched === false) return '-';
        const bands = computeSliderAcceptedRanges(
          handleRangeConfigFrom(block.data as never, handle, resolve),
        );
        return signatureForBands(side.value, bands);
      };
      const a = sign('a', pair.a);
      if (!a) return '';
      if (!dual) return a;
      const b = sign('b', pair.b);
      return b ? `${a}+${b}` : '';
    }

    // ── Sensors ──────────────────────────────────────────────────────────────────────────────
    case 'gps': {
      const reading = answer as { lat?: number; lng?: number };
      const data = block.data as Record<string, unknown>;
      if (typeof reading.lat !== 'number' || typeof reading.lng !== 'number') return '';
      // Stored as strings, and may be variable formulas, so they resolve like any other.
      const at = resolveField(data, roll);
      const lat = at(String(data.targetLat ?? ''), 'targetLat');
      const lng = at(String(data.targetLng ?? ''), 'targetLng');
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
      // Against the PRIMARY target even where branch targets exist: the signature asks how far
      // off the student was, not which target they were nearest, and a branch target changes
      // what the answer MEANS rather than what kind of attempt it was.
      //
      // METRES, never degrees: a degree of latitude is about 111km, so every attempt a student
      // could physically make would land in one bucket.
      const metres = haversineDistanceMeters({ lat, lng }, { lat: reading.lat, lng: reading.lng });
      return signatureForDistance(metres, at(String(data.toleranceRadius ?? ''), 'toleranceRadius'));
    }

    case 'compass': {
      const heading = typeof answer === 'number' ? answer : (answer as { heading?: number }).heading;
      if (typeof heading !== 'number') return '';
      const data = block.data as Record<string, unknown>;
      const answers = Array.isArray(data.answers) ? data.answers as Record<string, unknown>[] : [];
      const bands: AcceptedBand[] = answers.map(a => {
        const at = Number(resolveField(data, roll)(String(a.value ?? ''), `${a.id}.value`));
        // Per answer since the tolerance settings moved off the block. A signature is the
        // bucket a recorded attempt lands in, so reading the block's value here would file an
        // attempt under a band the grader never used.
        const asymmetric = answerSymmetry(a as { toleranceSymmetry?: 'symmetric' | 'asymmetric' }, data) === 'asymmetric';
        const below = Number(asymmetric ? a.toleranceBelow : a.toleranceValue) || 0;
        const above = Number(asymmetric ? a.toleranceAbove : a.toleranceValue) || 0;
        return { lo: at - below, hi: at + above, at };
      }).filter(b => Number.isFinite(b.at as number));
      return bands.length ? signatureForHeading(heading, bands) : '';
    }

    /*
     * THE TWO HALVES OF THE OLD `angle` BLOCK, and the reason this phase stopped.
     *
     * Signing Angle was blocked on its domain question: a circular [0,360] cannot express a
     * signed tilt, and bucketing a tilt the domain cannot represent would bake the wrong answer
     * into recorded data. The Tilt/Rotation split ANSWERED that question by making it two
     * questions, so each half now takes the family it always belonged to.
     */
    case 'tilt': {
      // LINEAR. The domain is -90..90 and a lean of -90 is nothing like one of +90, so the
      // antipodal collapse `signatureForHeading` performs would be exactly wrong here.
      const degrees = typeof answer === 'number' ? answer : undefined;
      if (typeof degrees !== 'number') return '';
      const resolve = resolveField(block.data as Record<string, unknown>, roll);
      const bands = computeTiltAcceptedRanges(tiltAcceptedRangeConfig(block.data as never, resolve));
      return bands.length ? signatureForBands(degrees, bands) : '';
    }

    case 'rotation': {
      // CIRCULAR, like compass: a swept path lives on [0,360], where 359 is next to 1.
      //
      // Signed even though the block is MOTHBALLED. `countsFor` refuses to build a task-level
      // signature when any answering block cannot produce one, so leaving this unsigned would
      // silently disable `unique` for every task still holding a rotation block — and
      // `MOTHBALLED_BLOCK_TYPES` says in as many words that a mothballed block is skipped by
      // DECISION, never by being left out of a list.
      const degrees = typeof answer === 'number' ? answer : undefined;
      if (typeof degrees !== 'number') return '';
      const resolve = resolveField(block.data as Record<string, unknown>, roll);
      const bands = computeRotationAcceptedRanges(rotationAcceptedRangeConfig(block.data as never, resolve));
      return bands.length ? signatureForHeading(degrees, bands) : '';
    }

    default:
      return '';
  }
}

/** Every signature for one submission, from the answers the registry collected. */
export function signaturesFor(
  blocks: Block[],
  answers: Record<string, unknown>,
  roll: Record<string, string> = {},
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const block of blocks) {
    const signature = attemptSignature(block, answers[block.id], roll);
    if (signature) out[block.id] = signature;
  }
  return out;
}
